import { SignedIn, SignedOut } from '@develio/auth/client';
import { Navigate } from 'react-router-dom';
import './LandingRoute.css';

/** While waitlist mode is on, "start" CTAs point at the waitlist instead of
 * signup. Flip VITE_WAITLIST_MODE in Vercel to toggle. */
const WAITLIST_MODE = import.meta.env.VITE_WAITLIST_MODE === 'true';

export function LandingRoute() {
  const startHref = WAITLIST_MODE ? '/waitlist' : '/sign-in';
  const startLabel = WAITLIST_MODE ? 'Join the waitlist' : 'Get Started';
  return (
    <>
      <SignedIn>
        <Navigate to="/dashboard" replace />
      </SignedIn>
      <SignedOut>
        <div className="landing">
          {/* NAV */}
          <nav className="landing-nav">
            <a href="/" className="nav-logo">
              <img src="/logo.png" alt="develio" style={{ height: '32px', width: 'auto' }} />
            </a>
            <div className="nav-links">
              <a href="#how">How It Works</a>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#proof">About</a>
              <a href="/sign-in" className="btn-nav">
                Sign In
              </a>
            </div>
          </nav>

          {/* HERO */}
          <section className="hero">
            <div className="hero-content">
              <div className="hero-badge">Economic Development Platform</div>
              <h1>
                Every project tells a story. <em>See the whole picture.</em>
              </h1>
              <p>
                Develio gives economic development teams a clear way to evaluate prospective
                projects. Score, compare, and present opportunities with the data your board
                actually needs.
              </p>
              <div className="hero-ctas">
                <a href={startHref} className="btn-primary">
                  {startLabel}
                </a>
                <a href="#how" className="btn-secondary">
                  See How It Works
                </a>
              </div>
            </div>
            <div className="hero-visual">
              <div className="hero-glow" />
              <div className="hero-dashboard">
                <div className="dash-bar">
                  <span className="dash-dot" />
                  <span className="dash-dot" />
                  <span className="dash-dot" />
                </div>
                <div className="dash-body">
                  <div className="dash-metric-row">
                    <div className="dash-metric">
                      <div className="dash-metric-label">Project Score</div>
                      <div className="dash-metric-value green">87</div>
                    </div>
                    <div className="dash-metric">
                      <div className="dash-metric-label">Est. Jobs</div>
                      <div className="dash-metric-value teal">340</div>
                    </div>
                    <div className="dash-metric">
                      <div className="dash-metric-label">ROI Index</div>
                      <div className="dash-metric-value orange">2.4x</div>
                    </div>
                  </div>
                  <div className="dash-chart">
                    <div className="dash-chart-label">Impact Comparison: Active Pipeline</div>
                    <div className="dash-bars">
                      <span style={{ height: '65%', background: 'var(--green)', opacity: 0.7 }} />
                      <span style={{ height: '85%', background: 'var(--green)' }} />
                      <span style={{ height: '45%', background: 'var(--teal)', opacity: 0.7 }} />
                      <span style={{ height: '70%', background: 'var(--teal)' }} />
                      <span style={{ height: '55%', background: 'var(--orange)', opacity: 0.7 }} />
                      <span style={{ height: '90%', background: 'var(--green)' }} />
                      <span style={{ height: '40%', background: 'var(--teal)', opacity: 0.6 }} />
                      <span style={{ height: '75%', background: 'var(--green)', opacity: 0.8 }} />
                      <span style={{ height: '60%', background: 'var(--orange)' }} />
                      <span style={{ height: '50%', background: 'var(--teal)', opacity: 0.7 }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* PAIN */}
          <section className="pain">
            <div className="pain-inner">
              <h2>Your community's future shouldn't depend on a spreadsheet</h2>
              <p>
                Most EDOs evaluate development projects with incomplete data, inconsistent scoring,
                and presentations built the night before a board meeting. When every project looks
                good on paper, the real question is which one is actually the best deal. That
                question rarely gets a straight answer.
              </p>
              <div className="pain-stats">
                <div>
                  <div className="pain-stat-value">73%</div>
                  <div className="pain-stat-label">
                    of EDOs still rely on spreadsheets for project evaluation
                  </div>
                </div>
                <div>
                  <div className="pain-stat-value">6–12 mo</div>
                  <div className="pain-stat-label">
                    average time from first contact to incentive decision
                  </div>
                </div>
                <div>
                  <div className="pain-stat-value">$4.2M</div>
                  <div className="pain-stat-label">
                    average incentive package offered without standardized analysis
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* HOW IT WORKS */}
          <section className="how" id="how">
            <div className="section-label">How It Works</div>
            <h2>From prospect to presentation in minutes</h2>
            <p>Three steps. One platform. Complete project clarity.</p>
            <div className="steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Input Project Details</h3>
                <p>
                  Enter the details that matter: jobs, capital investment, industry, timeline, and
                  incentive request. Develio's intake form captures everything your team needs to
                  run a complete evaluation.
                </p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Analyze &amp; Benchmark</h3>
                <p>
                  Develio scores the project across economic impact dimensions, benchmarks it
                  against your pipeline, and surfaces the metrics that matter most to your
                  community.
                </p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Present with Confidence</h3>
                <p>
                  Generate board-ready reports with clear scoring, side-by-side comparisons, and
                  concrete recommendations. No more late nights building decks from scratch.
                </p>
              </div>
            </div>
          </section>

          {/* FEATURES */}
          <section className="features" id="features">
            <div className="features-inner">
              <div className="section-label">Platform</div>
              <h2>Built for how EDOs actually work</h2>
              <p>
                Every feature designed around the real workflow of economic development
                professionals.
              </p>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#5BBF3A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                  </div>
                  <h3>Project Scoring Engine</h3>
                  <p>
                    Scoring across jobs, capital investment, wages, industry alignment, and
                    community fit, all weighted to your EDO's specific priorities.
                  </p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1B8A8A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                    </svg>
                  </div>
                  <h3>Pipeline Comparison</h3>
                  <p>
                    See how every new prospect compares to active and historical projects in your
                    portfolio. Side-by-side analysis that makes the real tradeoffs visible.
                  </p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#E8501A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
                      <polyline points="14,2 14,8 20,8" />
                    </svg>
                  </div>
                  <h3>Board-Ready Reports</h3>
                  <p>
                    One-click export to polished PDF presentations. Formatted, branded, and ready to
                    put in front of your board or city council.
                  </p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1B8A8A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                  </div>
                  <h3>Custom Weighting</h3>
                  <p>
                    Every community has different priorities. Adjust how jobs, wages, and capital
                    investment are weighted so the scores reflect what your region actually values.
                  </p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#5BBF3A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M12 20V10" />
                      <path d="M18 20V4" />
                      <path d="M6 20v-4" />
                    </svg>
                  </div>
                  <h3>Impact Modeling</h3>
                  <p>
                    Estimate the downstream effects: tax revenue, wage impact, and multiplier
                    effects, with assumptions you can actually explain to your board.
                  </p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#E8501A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87" />
                      <path d="M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  </div>
                  <h3>Team Collaboration</h3>
                  <p>
                    Share projects across your team with role-based access. Directors, analysts, and
                    board liaisons each see exactly what they need.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* PROOF */}
          <section className="proof" id="proof">
            <div className="proof-inner">
              <div className="section-label">Trusted by EDOs</div>
              <h2>Purpose-built for economic development</h2>
              <p className="proof-quote">
                We used to spend two weeks preparing project analysis for each board presentation.
                Develio cut that to an afternoon, and the output is more thorough than anything we
                were producing on our own.
              </p>
              <div className="proof-attrib">
                <strong>Economic Development Director</strong> · Texas EDC
              </div>
              <div className="proof-logos">
                <div className="proof-logo-placeholder">Partner EDO</div>
                <div className="proof-logo-placeholder">Partner EDO</div>
                <div className="proof-logo-placeholder">Partner EDO</div>
                <div className="proof-logo-placeholder">Partner EDO</div>
              </div>
            </div>
          </section>

          {/* PRICING */}
          <section className="pricing" id="pricing">
            <div className="pricing-inner">
              <div className="section-label">Pricing</div>
              <h2>Priced for every community</h2>
              <p className="pricing-sub">
                Start with a 14-day free trial of Professional. You are not charged until the trial
                ends, and you can cancel anytime. Both paid plans cost far less than the
                $6,500+/year alternatives.
              </p>
              <div className="pricing-grid">
                {/* 14-day trial */}
                <div className="price-card">
                  <div className="price-name">14-Day Trial</div>
                  <div className="price-amount">$0</div>
                  <div className="price-period">Full Professional access, then $299/mo</div>
                  <p className="price-tagline">See the whole picture before you pay.</p>
                  <ul className="price-features">
                    <li>2 full impact analyses (unlimited when you subscribe)</li>
                    <li>Full market intelligence</li>
                    <li>Branded PDF reports</li>
                    <li>Card required · cancel anytime</li>
                  </ul>
                  <a href={startHref} className="price-cta price-cta-secondary">
                    {WAITLIST_MODE ? 'Join the waitlist' : 'Start 14-day trial'}
                  </a>
                </div>
                {/* Professional */}
                <div className="price-card featured">
                  <div className="price-badge">Most popular</div>
                  <div className="price-name">Professional</div>
                  <div className="price-amount">
                    $299<span className="price-mo">/mo</span>
                  </div>
                  <div className="price-period">or $2,990/year (2 months free)</div>
                  <p className="price-tagline">Everything a working EDC needs.</p>
                  <ul className="price-features">
                    <li>Unlimited impact analyses</li>
                    <li>Community Health, Workforce &amp; Industry data</li>
                    <li>Branded PDF exports</li>
                    <li>AI narrative summaries</li>
                    <li>Data citations included</li>
                  </ul>
                  <a href={startHref} className="price-cta price-cta-primary">
                    {startLabel}
                  </a>
                </div>
                {/* Enterprise */}
                <div className="price-card">
                  <div className="price-name">Enterprise</div>
                  <div className="price-amount">
                    $599<span className="price-mo">/mo</span>
                  </div>
                  <div className="price-period">or $5,990/year (2 months free)</div>
                  <p className="price-tagline">For multi-jurisdiction teams.</p>
                  <ul className="price-features">
                    <li>Everything in Professional</li>
                    <li>Multiple jurisdictions</li>
                    <li>SAML / Single Sign-On</li>
                    <li>Custom RIMS data integration</li>
                    <li>Dedicated support + SLA</li>
                  </ul>
                  <a href={startHref} className="price-cta price-cta-secondary">
                    {startLabel}
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* CTA BANNER */}
          <section className="cta-banner">
            <div className="cta-banner-inner">
              <h2>Better data. Better decisions. Better outcomes.</h2>
              <p>
                Stop evaluating million-dollar development projects with hundred-dollar tools.
                Develio gives your team the analytical edge your community deserves.
              </p>
              <a href={startHref} className="btn-primary">
                {WAITLIST_MODE ? 'Join the waitlist' : 'Get Started Today'}
              </a>
            </div>
          </section>

          {/* FOOTER */}
          <footer className="landing-footer">
            <div className="footer-inner">
              <div className="footer-left">
                <img
                  src="/logo-white.png"
                  alt="develio"
                  style={{ height: '28px', width: 'auto' }}
                />
                <span className="footer-copy">&copy; 2026 Develio. All rights reserved.</span>
              </div>
              <div className="footer-links">
                <span>Privacy</span>
                <span>Terms</span>
                <span>Contact</span>
              </div>
            </div>
          </footer>
        </div>
      </SignedOut>
    </>
  );
}
